import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import {
  trackGateSeen,
  trackIntentCaptured,
  trackGateSubmit,
  trackWtpResponse,
  saveIntentRecord,
  saveMonetisationLeadRecord,
} from '../../utils/analytics/intentTracking';
import { captureMonetisationLead } from '../../services/leadCapture';
import { LEAD_SOURCES } from '../../services/contactApi';
import { INTENT_SEGMENTS, WTP_OPTIONS } from '../../types/monetisation';
import type {
  CalculationContextSnapshot,
  IntentSegment,
  WillingnessToPay,
} from '../../types/monetisation';

interface ExportIntentGateProps {
  isOpen: boolean;
  source: string;
  onClose: () => void;
  onProceed: () => void;
  /**
   * Snapshot of the calculation that triggered this export. Carried on the
   * captured lead record (AC fx-2.2#AC2). Optional so the gate can still render
   * in surfaces without a single canonical calculation.
   */
  calculationContext?: CalculationContextSnapshot;
}

const POPIA_NOTICE =
  'POPIA notice: if provided, your email is used for lead follow-up only. Submission is optional — leave it blank and your email is not captured. We will not share it with third parties.';

const FAIS_DISCLAIMER =
  'This is a calculation only and not financial advice.';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FieldErrors {
  email?: string;
  segment?: string;
  willingnessToPay?: string;
}

export default function ExportIntentGate({
  isOpen,
  source,
  onClose,
  onProceed,
  calculationContext,
}: ExportIntentGateProps) {
  const [email, setEmail] = useState('');
  const [segment, setSegment] = useState<IntentSegment | ''>('');
  const [willingnessToPay, setWillingnessToPay] = useState<WillingnessToPay | ''>('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const gateSeenRef = useRef(false);

  // Fire `gate_seen` exactly once when the form becomes visible.
  useEffect(() => {
    if (!isOpen) {
      gateSeenRef.current = false;
      return;
    }
    if (gateSeenRef.current) return;
    gateSeenRef.current = true;
    trackGateSeen(source);
  }, [isOpen, source]);

  if (!isOpen) return null;

  const clearFieldError = (field: keyof FieldErrors) => {
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: FieldErrors = {};
    const trimmedEmail = email.trim();

    if (trimmedEmail && !EMAIL_PATTERN.test(trimmedEmail)) {
      nextErrors.email = 'Please enter a valid email address, or leave it blank.';
    }
    if (!segment) {
      nextErrors.segment = 'Please select which best describes you.';
    }
    if (!willingnessToPay) {
      nextErrors.willingnessToPay = 'Please answer the willingness-to-pay question.';
    }

    if (Object.keys(nextErrors).length > 0) {
      // Invalid / incomplete submit: do NOT deliver the export; show field-level
      // validation (AC fx-2.2#AC5).
      setFieldErrors(nextErrors);
      return;
    }

    const resolvedSegment = segment as IntentSegment;
    const resolvedWtp = willingnessToPay as WillingnessToPay;

    // Local persistence (back-compat with the intent experiment) + analytics.
    saveIntentRecord({
      email: trimmedEmail,
      segment: resolvedSegment,
      willingnessToPay: resolvedWtp,
    });
    trackIntentCaptured({
      segment: resolvedSegment,
      willingnessToPay: resolvedWtp,
      hasEmail: trimmedEmail.length > 0,
    });

    // fx-2.2 submit signals: gate_submit + wtp_response (AC fx-2.2#AC4).
    trackGateSubmit(source);
    trackWtpResponse({
      segment: resolvedSegment,
      willingnessToPay: resolvedWtp,
    });

    // Build + persist the full lead record (email, segment, WTP, calc context)
    // and write it to the lead-capture path (ERPNext Lead tagged
    // `reverseTax-b2b` via the backend). Fire-and-forget so the export is not
    // blocked on the network; a local copy is persisted above so a failed
    // capture is recoverable.
    const leadRecord = {
      email: trimmedEmail,
      segment: resolvedSegment,
      willingnessToPay: resolvedWtp,
      source: LEAD_SOURCES.B2B,
      calculationContext,
    };
    saveMonetisationLeadRecord(leadRecord);
    captureMonetisationLead(leadRecord).catch(() => {
      // Best-effort capture; failure is non-fatal (record is persisted locally).
    });

    // Deliver the export exactly as the existing suite does (AC fx-2.2#AC1).
    onProceed();
  };

  const handleSkip = () => {
    setFieldErrors({});
    onProceed();
  };

  const emailInvalid = !!fieldErrors.email;
  const segmentInvalid = !!fieldErrors.segment;
  const wtpInvalid = !!fieldErrors.willingnessToPay;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="intent-gate-title"
    >
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <h2 id="intent-gate-title" className="text-xl font-bold text-charcoal dark:text-slate-100">
            Quick question before you export
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 text-2xl leading-none"
            aria-label="Close intent form"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5" noValidate>
          <p className="text-sm text-gray-600 dark:text-slate-300">
            We are exploring a detailed, always-current paid version. Tell us a bit about you so we
            can build the right thing.
          </p>

          <p
            id="intent-fais-disclaimer"
            className="text-xs text-gray-500 dark:text-slate-400 italic border-l-2 border-gray-300 dark:border-slate-600 pl-3"
          >
            {FAIS_DISCLAIMER}
          </p>

          <div>
            <label htmlFor="intent-email" className="block text-sm font-medium text-charcoal dark:text-slate-100 mb-1">
              Email <span className="text-gray-500 dark:text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              id="intent-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearFieldError('email');
              }}
              className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-900 text-charcoal dark:text-slate-100 focus:ring-2 focus:ring-sa-green focus:outline-none ${
                emailInvalid
                  ? 'border-error dark:border-error'
                  : 'border-gray-300 dark:border-slate-600'
              }`}
              placeholder="you@example.com"
              aria-describedby={emailInvalid ? 'intent-email-error intent-popia-notice' : 'intent-popia-notice'}
              aria-invalid={emailInvalid || undefined}
            />
            {emailInvalid && (
              <p id="intent-email-error" className="mt-1 text-sm text-error" role="alert">
                {fieldErrors.email}
              </p>
            )}
            <p id="intent-popia-notice" className="mt-1 text-xs text-gray-500 dark:text-slate-400">
              {POPIA_NOTICE}
            </p>
          </div>

          <div>
            <label htmlFor="intent-segment" className="block text-sm font-medium text-charcoal dark:text-slate-100 mb-1">
              Which best describes you?
            </label>
            <select
              id="intent-segment"
              value={segment}
              onChange={(e) => {
                setSegment(e.target.value as IntentSegment | '');
                clearFieldError('segment');
              }}
              className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-900 text-charcoal dark:text-slate-100 focus:ring-2 focus:ring-sa-green focus:outline-none ${
                segmentInvalid
                  ? 'border-error dark:border-error'
                  : 'border-gray-300 dark:border-slate-600'
              }`}
              aria-describedby={segmentInvalid ? 'intent-segment-error' : undefined}
              aria-invalid={segmentInvalid || undefined}
            >
              <option value="">Select an option</option>
              {INTENT_SEGMENTS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {segmentInvalid && (
              <p id="intent-segment-error" className="mt-1 text-sm text-error" role="alert">
                {fieldErrors.segment}
              </p>
            )}
          </div>

          <fieldset>
            <legend className="block text-sm font-medium text-charcoal dark:text-slate-100 mb-2">
              How likely are you to pay for a detailed version?
            </legend>
            <div
              className="space-y-2"
              role="radiogroup"
              aria-label="Willingness to pay"
              aria-describedby={wtpInvalid ? 'intent-wtp-error' : undefined}
            >
              {WTP_OPTIONS.map((opt) => {
                const checked = willingnessToPay === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-center px-3 py-2 rounded-lg border cursor-pointer ${
                      checked
                        ? 'border-sa-green bg-green-50 dark:bg-green-900/30'
                        : wtpInvalid
                          ? 'border-error dark:border-error'
                          : 'border-gray-300 dark:border-slate-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="willingness-to-pay"
                      value={opt.value}
                      checked={checked}
                      onChange={() => {
                        setWillingnessToPay(opt.value);
                        clearFieldError('willingnessToPay');
                      }}
                      className="mr-3"
                    />
                    <span className="text-sm text-gray-700 dark:text-slate-300">{opt.label}</span>
                  </label>
                );
              })}
            </div>
            {wtpInvalid && (
              <p id="intent-wtp-error" className="mt-1 text-sm text-error" role="alert">
                {fieldErrors.willingnessToPay}
              </p>
            )}
          </fieldset>

          <div className="flex flex-col sm:flex-row-reverse gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1">
              Continue to export
            </button>
            <button
              type="button"
              onClick={handleSkip}
              className="btn-secondary flex-1"
            >
              Skip for now
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
